import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert, Badge, Modal, Button, Table } from 'react-bootstrap';
import { 
  FaUsers, 
  FaCalendarCheck, 
  FaMapMarkerAlt,
  FaChartLine,
  FaCalendarAlt
} from 'react-icons/fa';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title, 
  Tooltip, 
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../context/ApiContext';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const { user } = useAuth();
  const { apiCall } = useApi();
  
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsersWithDateRanges: 0,
    cityDistribution: [],
    monthlyRegistrations: []
  });
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load visa users data
      const usersResponse = await apiCall('GET', '/api/visa-users?source=dashboard');
      const usersData = usersResponse.data || [];

      // Calculate statistics
      const totalUsers = usersData.length;
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Bugünün başlangıcı
      
      // Tarih aranan aktif kullanıcılar (randevusu geçmemiş)
      const activeUsersWithDateRanges = usersData.filter(u => {
        const isActiveWithDateRange = u.is_active === 1 && u.dateRangeCount && u.dateRangeCount > 0;
        
        if (!isActiveWithDateRange) return false;
        
        // Eğer randevu tarihi varsa, geçmemiş olmalı
        if (u.appointment_date) {
          const appointmentDate = new Date(u.appointment_date);
          appointmentDate.setHours(0, 0, 0, 0);
          
          // Randevu tarihi geçmişse dahil etme
          if (appointmentDate < today) {
            return false;
          }
        }
        
        return true;
      }).length;

      // City distribution (aktif, tarih aranan ve randevusu geçmemiş kullanıcılar)
      const cityCount = {};
      
      usersData.forEach(u => {
        // Aktif, tarih aranan ve randevu tarihi geçmemiş kullanıcılar
        const isActiveWithDateRange = u.is_active === 1 && u.dateRangeCount && u.dateRangeCount > 0;
        
        if (isActiveWithDateRange && u.appointment_city) {
          let includeUser = true;
          
          // Eğer randevu tarihi varsa, geçmemiş olmalı
          if (u.appointment_date) {
            const appointmentDate = new Date(u.appointment_date);
            appointmentDate.setHours(0, 0, 0, 0);
            
            // Randevu tarihi geçmişse dahil etme
            if (appointmentDate < today) {
              includeUser = false;
            }
          }
          
          if (includeUser) {
            cityCount[u.appointment_city] = (cityCount[u.appointment_city] || 0) + 1;
          }
        }
      });
      
      const cityDistribution = Object.entries(cityCount).map(([city, count]) => ({
        city,
        count
      }));

      // Monthly registrations (last 6 months)
      const monthlyCount = {};
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        // Use local timezone to match created_date format
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const key = `${year}-${month}`; // YYYY-MM format
        monthlyCount[key] = 0;
      }
      
      usersData.forEach(u => {
        if (u.created_date) {
          const month = u.created_date.slice(0, 7);
          if (monthlyCount.hasOwnProperty(month)) {
            monthlyCount[month]++;
          }
        }
      });

      const monthlyRegistrations = Object.entries(monthlyCount).map(([month, count]) => ({
        month,
        count
      }));

      setStats({
        totalUsers,
        activeUsersWithDateRanges,
        cityDistribution,
        monthlyRegistrations
      });

      // Get appointments with dates
      const appointmentsData = usersData.filter(u => u.appointment_date).map(u => ({
        id: u.id,
        date: u.appointment_date,
        username: u.username,
        applicant_name: u.applicant_name,
        city: u.appointment_city
      }));
      
      console.log('Appointments found:', appointmentsData);
      setAppointments(appointmentsData);

    } catch (err) {
      console.error('Dashboard data loading error:', err);
      setError(err.message || 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calendar helper functions
  const getAppointmentsForDate = (date) => {
    // Format calendar date as YYYY-MM-DD in local timezone (avoid UTC conversion)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const calendarDateStr = `${year}-${month}-${day}`;
    
    const result = appointments.filter(apt => {
      if (!apt.date) return false;
      
      // Normalize appointment date - handle both "YYYY-MM-DD HH:mm:ss" and "YYYY-MM-DDTHH:mm:ss" formats
      let aptDateStr = apt.date;
      if (aptDateStr.includes('T')) {
        aptDateStr = aptDateStr.split('T')[0];
      } else if (aptDateStr.includes(' ')) {
        aptDateStr = aptDateStr.split(' ')[0];
      }
      
      return aptDateStr === calendarDateStr;
    });
    
    return result;
  };

  const getTileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
    const appts = getAppointmentsForDate(date);
    if (appts.length > 0) {
      return 'has-appointment';
    }
    return null;
  };

  const handleDateClick = (date) => {
    const appts = getAppointmentsForDate(date);
    if (appts.length > 0) {
      setSelectedDate(date);
      setShowAppointmentModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowAppointmentModal(false);
  };

  const getApplicantCount = (applicantName) => {
    if (!applicantName) return 1;
    return applicantName.split('|').length;
  };

  const formatAppointmentTime = (dateString) => {
    if (!dateString) return '-';
    // Format: "2027-06-08 10:00:00" or "2027-06-08T10:00:00"
    const timePart = dateString.includes('T') 
      ? dateString.split('T')[1].split('.')[0] 
      : dateString.split(' ')[1];
    
    if (timePart) {
      const [hours, minutes] = timePart.split(':');
      return `${hours}:${minutes}`;
    }
    return '-';
  };

  const getTileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const appts = getAppointmentsForDate(date);
    
    if (appts.length > 0) {
      return (
        <div style={{
          fontSize: '0.7rem',
          fontWeight: 'bold',
          color: '#fff',
          backgroundColor: '#0d6efd',
          borderRadius: '50%',
          width: '18px',
          height: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          marginTop: '2px'
        }}>
          {appts.length}
        </div>
      );
    }
    return null;
  };

  // Chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      },
    },
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-4">
        <Alert.Heading>Hata!</Alert.Heading>
        {error}
      </Alert>
    );
  }

  // Prepare chart data
  const monthlyRegistrationsChartData = {
    labels: stats.monthlyRegistrations.map(m => {
      const [year, month] = m.month.split('-');
      return new Date(year, month - 1).toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' });
    }),
    datasets: [
      {
        label: 'Yeni Kullanıcılar',
        data: stats.monthlyRegistrations.map(m => m.count),
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13, 110, 253, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  return (
    <div className="p-2 p-md-4">
      {/* Header */}
      <div className="mb-3 mb-md-4">
        <h2 className="d-flex align-items-center fs-4 fs-md-2">
          <FaChartLine className="me-2 text-primary" />
          Dashboard
        </h2>
          <p className="text-muted mb-0 small">Hoş geldiniz, {user?.first_name || user?.email}</p>
      </div>

      {/* Overview Cards */}
      <Row className="g-3 g-md-4 mb-3 mb-md-4">
        <Col md={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm" style={{ 
            background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
            color: 'white'
          }}>
            <Card.Body className="p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <p className="mb-2 opacity-75 small">Toplam Kullanıcı</p>
                  <h1 className="mb-0 fw-bold fs-2 fs-md-1">{stats.totalUsers}</h1>
                </div>
                <div className="bg-white bg-opacity-25 p-2 p-md-3 rounded">
                  <FaUsers size={window.innerWidth < 768 ? 24 : 32} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm" style={{ 
            background: 'linear-gradient(135deg, #198754 0%, #157347 100%)',
            color: 'white'
          }}>
            <Card.Body className="p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1 pe-2">
                  <p className="mb-2 opacity-75 small">Tarih Aranan Aktif</p>
                  <h1 className="mb-0 fw-bold fs-2 fs-md-1">{stats.activeUsersWithDateRanges}</h1>
                  <small className="opacity-75 d-block" style={{ fontSize: '0.75rem' }}>
                    Randevusu geçmemiş
                  </small>
              </div>
                <div className="bg-white bg-opacity-25 p-2 p-md-3 rounded">
                  <FaCalendarCheck size={window.innerWidth < 768 ? 24 : 32} />
              </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm" style={{ 
            background: 'linear-gradient(135deg, #6f42c1 0%, #5a32a3 100%)',
            color: 'white'
          }}>
            <Card.Body className="p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <p className="mb-2 opacity-75 small">İstanbul</p>
                  <h1 className="mb-0 fw-bold fs-2 fs-md-1">
                    {stats.cityDistribution.find(c => c.city === 'İstanbul' || c.city === 'Istanbul')?.count || 0}
                  </h1>
                  <small className="opacity-75 d-block" style={{ fontSize: '0.75rem' }}>
                    Aktif kullanıcı
                  </small>
                </div>
                <div className="bg-white bg-opacity-25 p-2 p-md-3 rounded">
                  <FaMapMarkerAlt size={window.innerWidth < 768 ? 24 : 32} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card className="h-100 border-0 shadow-sm" style={{ 
            background: 'linear-gradient(135deg, #fd7e14 0%, #dc6502 100%)',
            color: 'white'
          }}>
            <Card.Body className="p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <p className="mb-2 opacity-75 small">Ankara</p>
                  <h1 className="mb-0 fw-bold fs-2 fs-md-1">
                    {stats.cityDistribution.find(c => c.city === 'Ankara' || c.city === 'ankara')?.count || 0}
                  </h1>
                  <small className="opacity-75 d-block" style={{ fontSize: '0.75rem' }}>
                    Aktif kullanıcı
                  </small>
                </div>
                <div className="bg-white bg-opacity-25 p-2 p-md-3 rounded">
                  <FaMapMarkerAlt size={window.innerWidth < 768 ? 24 : 32} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Yaklaşan Randevular - Calendar */}
      <Row className="g-3 g-md-4 mb-3 mb-md-4">
        <Col lg={7}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom p-3 p-md-4">
              <h5 className="mb-0 d-flex align-items-center fs-6 fs-md-5">
                <FaCalendarAlt className="me-2 text-primary" />
                Yaklaşan Randevular
              </h5>
            </Card.Header>
            <Card.Body className="p-3 p-md-4">
              {appointments.length > 0 ? (
                <Alert variant="info" className="mb-3">
                  <small>
                    📌 <strong>{appointments.length}</strong> randevu bulundu. 
                    Mavi renkli günlere tıklayarak detayları görebilirsiniz.
                  </small>
                </Alert>
              ) : (
                <Alert variant="warning" className="mb-3">
                  <small>⚠️ Henüz randevu tarihi olan kullanıcı bulunmamaktadır.</small>
                </Alert>
              )}
              
              <style>{`
                .react-calendar {
                  width: 100%;
                  border: none;
                  font-family: inherit;
                }
                .react-calendar__tile {
                  padding: 15px 5px;
                  font-size: 0.9rem;
                  cursor: default;
                }
                .react-calendar__tile.has-appointment {
                  background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
                  color: white;
                  font-weight: 600;
                  border-radius: 8px;
                  cursor: pointer;
                }
                .react-calendar__tile.has-appointment:hover {
                  background: linear-gradient(135deg, #0a58ca 0%, #0845a0 100%);
                  transform: scale(1.05);
                  transition: all 0.2s ease;
                }
                .react-calendar__tile:not(.has-appointment) {
                  pointer-events: none;
                }
                .react-calendar__tile--active {
                  background: #0d6efd !important;
                  color: white;
                }
                .react-calendar__navigation button {
                  font-size: 1rem;
                  font-weight: 600;
                }
                
                /* Mobile responsive */
                @media (max-width: 768px) {
                  .react-calendar__tile {
                    padding: 10px 2px;
                    font-size: 0.8rem;
                  }
                  .react-calendar__navigation button {
                    font-size: 0.9rem;
                  }
                  .react-calendar__month-view__days__day--neighboringMonth {
                    opacity: 0.3;
                  }
                }
              `}</style>
              <Calendar
                key={appointments.length} // Force re-render when appointments change
                onChange={handleDateClick}
                tileClassName={getTileClassName}
                tileContent={getTileContent}
                locale="tr-TR"
              />
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom p-3 p-md-4">
              <h5 className="mb-0 d-flex align-items-center fs-6 fs-md-5">
                <FaCalendarCheck className="me-2 text-success" />
                Yaklaşan Randevular
              </h5>
              <small className="text-muted">Tarih sıralamasına göre</small>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: window.innerWidth < 768 ? '300px' : '400px', overflowY: 'auto' }}>
              {appointments.length > 0 ? (
                <div className="list-group list-group-flush">
                  {[...appointments]
                    .filter(apt => {
                      // Sadece gelecekteki randevuları göster
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const aptDate = new Date(apt.date);
                      aptDate.setHours(0, 0, 0, 0);
                      return aptDate >= today;
                    })
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(0, 10)
                    .map((apt, index) => (
                      <div 
                        key={apt.id || index} 
                        className="list-group-item list-group-item-action border-0 border-bottom py-3"
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <Badge bg="primary" className="me-2 px-2 py-1" style={{ fontSize: '0.7rem' }}>
                                {index + 1}
                              </Badge>
                              <strong className="text-dark">{apt.username}</strong>
                            </div>
                            <div className="d-flex flex-wrap gap-2 align-items-center small text-muted">
                              <span className="d-flex align-items-center">
                                <FaCalendarAlt className="me-1" size={12} />
                                {new Date(apt.date).toLocaleDateString('tr-TR', { 
                                  day: 'numeric', 
                                  month: 'short', 
                                  year: 'numeric' 
                                })}
                              </span>
                              <span className="d-flex align-items-center">
                                <FaMapMarkerAlt className="me-1" size={12} />
                                {apt.city || '-'}
                              </span>
                              {apt.applicant_name && (
                                <Badge bg="success" pill style={{ fontSize: '0.65rem' }}>
                                  {getApplicantCount(apt.applicant_name)} kişi
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Badge bg="info" className="ms-2">
                            {formatAppointmentTime(apt.date)}
                          </Badge>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-center text-muted p-4" style={{ minHeight: '200px' }}>
                  <div className="text-center">
                    <FaCalendarAlt size={48} className="mb-3 opacity-25" />
                    <p>Yaklaşan randevu bulunamadı</p>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Aylık Eklenen Kullanıcılar */}
      <Row className="g-3 g-md-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom p-3 p-md-4">
              <h5 className="mb-0 d-flex align-items-center fs-6 fs-md-5">
                <FaChartLine className="me-2 text-info" />
                <span className="d-none d-md-inline">Aylık Eklenen Kullanıcılar (Son 6 Ay)</span>
                <span className="d-inline d-md-none">Aylık Kullanıcılar</span>
              </h5>
            </Card.Header>
            <Card.Body className="p-3 p-md-4">
              <div style={{ height: window.innerWidth < 768 ? '250px' : '300px' }}>
                <Line data={monthlyRegistrationsChartData} options={chartOptions} />
                </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Randevu Detay Modal */}
      <Modal show={showAppointmentModal} onHide={handleCloseModal} centered size="xl" fullscreen="md-down">
        <Modal.Header closeButton className="border-0 pb-2 p-3 p-md-4" style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)' }}>
          <Modal.Title className="d-flex align-items-center text-white fs-6 fs-md-5">
            <FaCalendarCheck className="me-2" size={window.innerWidth < 768 ? 18 : 24} />
            {selectedDate && (
              <div>
                <div className="fs-6 fs-md-5">
                  {selectedDate.toLocaleDateString('tr-TR', { 
                    day: 'numeric', 
                    month: window.innerWidth < 768 ? 'short' : 'long', 
                    year: 'numeric'
                  })}
                  {window.innerWidth >= 768 && ` - ${selectedDate.toLocaleDateString('tr-TR', { weekday: 'long' })}`}
                </div>
                <small className="opacity-75" style={{ fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem' }}>
                  {selectedDate && getAppointmentsForDate(selectedDate).length} Randevu
                </small>
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedDate && (
            <div className="table-responsive">
              <Table hover className="mb-0" size={window.innerWidth < 768 ? 'sm' : undefined}>
                <thead className="bg-light">
                  <tr>
                    <th className="ps-3 ps-md-4 py-2 py-md-3" style={{ width: '40px' }}>#</th>
                    <th className="py-2 py-md-3 small">Kullanıcı Adı</th>
                    <th className="py-2 py-md-3 text-center small">Saat</th>
                    <th className="py-2 py-md-3 small d-none d-md-table-cell">Şehir</th>
                    <th className="py-2 py-md-3 text-center small d-none d-lg-table-cell">Kişi Sayısı</th>
                    <th className="py-2 py-md-3 pe-3 pe-md-4 small d-none d-lg-table-cell">Başvuranlar</th>
                  </tr>
                </thead>
                <tbody>
                  {getAppointmentsForDate(selectedDate).map((apt, index) => (
                    <tr key={apt.id || index}>
                      <td className="ps-3 ps-md-4 align-middle">
                        <Badge bg="primary" className="px-2 py-1" style={{ fontSize: window.innerWidth < 768 ? '0.7rem' : '0.875rem' }}>
                          {index + 1}
                        </Badge>
                      </td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center">
                          <div 
                            className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-2 d-none d-md-flex"
                            style={{ width: '32px', height: '32px', minWidth: '32px' }}
                          >
                            <FaUsers className="text-white" size={14} />
                          </div>
                          <strong className="small">{apt.username}</strong>
                        </div>
                      </td>
                      <td className="align-middle text-center">
                        <Badge bg="info" className="px-2 px-md-3 py-1 py-md-2" style={{ fontSize: window.innerWidth < 768 ? '0.75rem' : '0.9rem' }}>
                          {formatAppointmentTime(apt.date)}
                        </Badge>
                      </td>
                      <td className="align-middle d-none d-md-table-cell">
                        <FaMapMarkerAlt className="text-info me-2" size={14} />
                        <span className="small">{apt.city || 'Belirtilmemiş'}</span>
                      </td>
                      <td className="align-middle text-center d-none d-lg-table-cell">
                        <Badge bg="success" className="px-3 py-2">
                          {getApplicantCount(apt.applicant_name)} Kişi
                        </Badge>
                      </td>
                      <td className="align-middle pe-3 pe-md-4 d-none d-lg-table-cell">
                        {apt.applicant_name ? (
                          <div className="d-flex flex-wrap gap-1">
                            {apt.applicant_name.split('|').slice(0, 3).map((name, idx) => (
                              <Badge 
                                key={idx} 
                                bg="light" 
                                text="dark" 
                                className="px-2 py-1"
                                style={{ fontSize: '0.75rem', fontWeight: 'normal' }}
                              >
                                {name.trim()}
                              </Badge>
                            ))}
                            {apt.applicant_name.split('|').length > 3 && (
                              <Badge 
                                bg="secondary" 
                                className="px-2 py-1"
                                style={{ fontSize: '0.75rem' }}
                              >
                                +{apt.applicant_name.split('|').length - 3}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 bg-light p-3 p-md-4">
          <Button variant="secondary" onClick={handleCloseModal} size={window.innerWidth < 768 ? 'sm' : undefined}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Dashboard; 